import { useMutation, useQueryClient } from "@tanstack/react-query";

import useApiHelper from "../../hooks/useApiHelper";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";
import useNotification from "../../components/shared/Notifications/useNotifications";
import {
  IAddTicketNoteRequest,
  IBulkUpdateTicketStatusRequest,
  ICreateTicketRequest,
  IMergeTicketsRequest,
  ITicket,
  IUpdateTicketRequest,
} from "../../models/Tickets/Ticket";
import { TicketQueryKeys } from "./keys";
import { IDefaultResponse } from "../../models/DefaultResponse";
import { errorResolver } from "../../utils/errorResolver";

interface IAddTicketNoteMutation {
  ticketId: string;
  note: IAddTicketNoteRequest;
}

interface IAssignTicketMutation {
  ticketId: string;
  assignedToUserId?: string;
}

export default function useTicketMutations() {
  const queryClient = useQueryClient();
  const { saveHandlers } = useDefaultCRUDHandlers("Ticket");
  const { addNotification } = useNotification();
  const { put, post } = useApiHelper();

  const invalidateTicket = (ticketId?: string) => {
    queryClient.invalidateQueries({ queryKey: TicketQueryKeys.all() });
    if (ticketId) {
      queryClient.invalidateQueries({
        queryKey: TicketQueryKeys.single(ticketId),
      });
    }
  };

  const create = useMutation({
    mutationFn: (ticket: ICreateTicketRequest) =>
      post<IDefaultResponse<ITicket>, ICreateTicketRequest>(`/ticket`, ticket),
    onSuccess: (response) => {
      invalidateTicket(response?.data?.id);
      saveHandlers.onSuccess();
    },
    onError: () => {
      saveHandlers.onError();
    },
  });

  const update = useMutation({
    mutationFn: ({ id, ...changes }: IUpdateTicketRequest) =>
      put(`/ticket/${id}`, changes),
    onSuccess: (_, updateInput) => {
      invalidateTicket(updateInput.id);
      saveHandlers.onSuccess();
    },
    onError: () => {
      saveHandlers.onError();
    },
  });

  /**
   * Assigns to the signed in user. The API takes the email from the verified
   * Firebase token, so nothing needs sending in the body.
   */
  const assignToMe = useMutation({
    mutationFn: (ticketId: string) =>
      post<IDefaultResponse<ITicket>, undefined>(
        `/ticket/${ticketId}/assign/me`,
        undefined,
      ),
    onSuccess: (_, ticketId) => {
      invalidateTicket(ticketId);
      saveHandlers.onSuccess();
    },
    onError: () => {
      saveHandlers.onError();
    },
  });

  /** Pass an empty assignedToUserId to unassign. */
  const assign = useMutation({
    mutationFn: ({ ticketId, assignedToUserId }: IAssignTicketMutation) =>
      post(`/ticket/${ticketId}/assign`, { assignedToUserId }),
    onSuccess: (_, input) => {
      invalidateTicket(input.ticketId);
      saveHandlers.onSuccess();
    },
    onError: () => {
      saveHandlers.onError();
    },
  });

  const bulkHandlers = {
    onSuccess: (response: IDefaultResponse<number>) => {
      invalidateTicket();
      addNotification({
        variant: "success",
        primaryText: response.message ?? "Tickets updated",
      });
    },
    onError: (error: unknown) => {
      addNotification({ variant: "error", primaryText: errorResolver(error) });
    },
  };

  const bulkUpdateStatus = useMutation({
    mutationFn: (request: IBulkUpdateTicketStatusRequest) =>
      post<IDefaultResponse<number>, IBulkUpdateTicketStatusRequest>(
        `/ticket/bulk/status`,
        request,
      ),
    ...bulkHandlers,
  });

  /** As with assignToMe, the API takes the email from the Firebase token. */
  const bulkAssignToMe = useMutation({
    mutationFn: (ticketIds: string[]) =>
      post<IDefaultResponse<number>, { ticketIds: string[] }>(
        `/ticket/bulk/assign/me`,
        { ticketIds },
      ),
    ...bulkHandlers,
  });

  /** Errors are left to the caller - the merge modal shows them inline. */
  const merge = useMutation({
    mutationFn: (request: IMergeTicketsRequest) =>
      post<IDefaultResponse<ITicket>, IMergeTicketsRequest>(
        `/ticket/merge`,
        request,
      ),
    onSuccess: () => {
      invalidateTicket();
    },
  });

  const addNote = useMutation({
    mutationFn: ({ ticketId, note }: IAddTicketNoteMutation) =>
      post(`/ticket/${ticketId}/notes`, note),
    onSuccess: (_, updateInput) => {
      invalidateTicket(updateInput.ticketId);
      saveHandlers.onSuccess();
    },
    onError: () => {
      saveHandlers.onError();
    },
  });

  return {
    create,
    update,
    assign,
    assignToMe,
    bulkUpdateStatus,
    bulkAssignToMe,
    merge,
    addNote,
  };
}
